/**
 * Brokerage contracts repository — Prisma-first (NestJS is the system of record).
 *
 * This domain is Nest-only: there is no `public.brokerage_contracts` Supabase
 * table (the #1344 Supabase migrations were never merged). Each method requires
 * VITE_API_URL to be configured — if it is null, a clear error is thrown so the
 * caller surfaces the misconfiguration instead of silently returning nothing.
 *
 * Pattern: mirrors `nestRequest` / `nestMultipartRequest` from contracts.ts but
 * without any Supabase fallback.
 *
 * See: docs/manifests/manifest-6-supabase-to-nestjs.md
 */
import { makeNestRequest } from "@/services/nestRequest";
import { API_ROUTES } from "@/api/routes";
import {
  brokerageContractSchema,
  propertyBrokerageLinkSchema,
  extractedBrokerageFieldsSchema,
  propertyBrokerageStatusSchema,
  unitBrokerageStatusSchema,
} from "@dallali/shared";
import { z } from "zod";
import type {
  BrokerageContract,
  PropertyBrokerageLink,
  ExtractedBrokerageFields,
  CreateBrokerageContract,
  UpdateBrokerageContract,
  LinkPropertyBrokerage,
  PropertyBrokerageStatus,
  UnitBrokerageStatus,
} from "@dallali/shared";


// Authenticated Nest JSON request bound to the "brokerageContracts" slice —
// shared transport (timeout + retry + typed NestRequestError). The multipart
// upload helper below stays bespoke (FormData body, no JSON Content-Type).
const nestRequest = makeNestRequest("brokerageContracts");

// Authenticated Nest multipart (FormData) request — shared transport with JSON
// Content-Type disabled so the browser sets the multipart boundary automatically.
// See: https://developer.mozilla.org/en-US/docs/Web/API/FormData/Using_FormData_Objects
// As a POST it is NOT auto-retried (the shared helper only retries idempotent reads),
// preserving single-attempt upload semantics. The thin wrapper keeps call sites
// `(path, form)`.
const nestMultipart = makeNestRequest("brokerageContracts", { json: false });
function nestMultipartRequest<T>(path: string, formData: FormData): Promise<T> {
  return nestMultipart<T>(path, { method: "POST", body: formData });
}

/**
 * Optional client-side filters consumed by `useBrokerageContracts`. The Nest
 * `list` endpoint itself takes no params (it returns every contract for the
 * company); these are applied in the hook's React Query `select`.
 */
export interface BrokerageContractListFilters {
  status?: string;
  owner_id?: string;
}

export const brokerageContractsRepository = {
  /**
   * GET /brokerage-contracts
   * Returns every brokerage contract for the authenticated company (the guarded
   * endpoint scopes by JWT company; no query-param filtering server-side).
   * Requires VITE_API_URL.
   */
  async list(): Promise<BrokerageContract[]> {
    const res = await nestRequest<unknown>(API_ROUTES.brokerageContracts.list());
    return z.array(brokerageContractSchema).parse(res);
  },

  /**
   * GET /brokerage-contracts/:id
   * Returns a single brokerage contract (includes property_links).
   * Cross-tenant → 404. Requires VITE_API_URL.
   */
  async getById(id: string): Promise<BrokerageContract> {
    const res = await nestRequest<unknown>(
      API_ROUTES.brokerageContracts.getById(id),
    );
    return brokerageContractSchema.parse(res);
  },

  /**
   * PUT /brokerage-contracts/:id (JSON)
   * Partial update — all fields optional.
   * Requires VITE_API_URL.
   */
  async update(id: string, payload: UpdateBrokerageContract): Promise<BrokerageContract> {
    const res = await nestRequest<unknown>(
      API_ROUTES.brokerageContracts.update(id),
      { method: "PUT", body: JSON.stringify(payload) },
    );
    return brokerageContractSchema.parse(res);
  },

  /**
   * DELETE /brokerage-contracts/:id — soft-delete (204 No Content).
   * Requires VITE_API_URL.
   */
  async remove(id: string): Promise<void> {
    await nestRequest<void>(
      API_ROUTES.brokerageContracts.remove(id),
      { method: "DELETE" },
    );
  },

  /**
   * DELETE /brokerage-contracts/:id/links/:linkId — unlinks a property (204).
   * Requires VITE_API_URL.
   */
  async unlinkProperty(id: string, linkId: string): Promise<void> {
    await nestRequest<void>(
      API_ROUTES.brokerageContracts.unlink(id, linkId),
      { method: "DELETE" },
    );
  },

  /**
   * GET /brokerage-contracts/status/:propertyId
   * Returns the effective brokerage status for a single property.
   * Requires VITE_API_URL.
   */
  async getStatusForProperty(propertyId: string): Promise<PropertyBrokerageStatus> {
    const res = await nestRequest<unknown>(
      API_ROUTES.brokerageContracts.statusForProperty(propertyId),
    );
    return propertyBrokerageStatusSchema.parse(res);
  },

  /**
   * POST /brokerage-contracts/status { property_ids: string[] }
   * Batched, N+1-free status resolution for a set of properties.
   * The wire shape is an ARRAY of PropertyBrokerageStatus (one row per
   * requested id, each carrying its own property_id) — keyed here into a
   * map of propertyId → PropertyBrokerageStatus for O(1) lookups.
   * Requires VITE_API_URL.
   */
  async getBatchStatus(propertyIds: string[]): Promise<Record<string, PropertyBrokerageStatus>> {
    const res = await nestRequest<unknown>(
      API_ROUTES.brokerageContracts.batchStatus(),
      { method: "POST", body: JSON.stringify({ property_ids: propertyIds }) },
    );
    const rows = z.array(propertyBrokerageStatusSchema).parse(res);
    return Object.fromEntries(rows.map((row) => [row.property_id, row]));
  },

  /**
   * GET /brokerage-contracts/status/unit/:unitId
   * Effective brokerage status for a single UNIT — its own unit-scoped
   * contracts plus the whole-property ones it inherits. `coverage` says which
   * of the two is driving the verdict ('unit' | 'property' | 'none').
   * Requires VITE_API_URL.
   */
  async getStatusForUnit(unitId: string): Promise<UnitBrokerageStatus> {
    const res = await nestRequest<unknown>(
      API_ROUTES.brokerageContracts.statusForUnit(unitId),
    );
    return unitBrokerageStatusSchema.parse(res);
  },

  /**
   * POST /brokerage-contracts/status/units { unit_ids: string[] }
   * Batched, N+1-free unit status (unit grids/list views). Same wire contract as
   * getBatchStatus: an ARRAY of rows, keyed here into unitId → status for O(1)
   * lookups. Requires VITE_API_URL.
   */
  async getBatchUnitStatus(unitIds: string[]): Promise<Record<string, UnitBrokerageStatus>> {
    const res = await nestRequest<unknown>(
      API_ROUTES.brokerageContracts.batchUnitStatus(),
      { method: "POST", body: JSON.stringify({ unit_ids: unitIds }) },
    );
    const rows = z.array(unitBrokerageStatusSchema).parse(res);
    return Object.fromEntries(rows.map((row) => [row.unit_id, row]));
  },

  /**
   * POST /brokerage-contracts/extract (multipart field `file`)
   * Returns a best-effort ExtractedBrokerageFields stub — all fields may be null.
   * Requires VITE_API_URL.
   */
  async extract(file: File): Promise<ExtractedBrokerageFields> {
    const formData = new FormData();
    formData.append("file", file);
    const res = await nestMultipartRequest<unknown>(API_ROUTES.brokerageContracts.extract(), formData);
    return extractedBrokerageFieldsSchema.parse(res);
  },

  /**
   * POST /brokerage-contracts (JSON)
   * Creates a brokerage contract. owner_id is required. company_id and
   * property_id are NOT accepted (company derived from JWT; property attaches
   * via linkProperty).
   * Requires VITE_API_URL.
   */
  async create(payload: CreateBrokerageContract): Promise<BrokerageContract> {
    const res = await nestRequest<unknown>(API_ROUTES.brokerageContracts.create(), {
      method: "POST",
      body: JSON.stringify(payload),
    });
    return brokerageContractSchema.parse(res);
  },

  /**
   * POST /brokerage-contracts/:id/document (multipart field `file`)
   * Stores the PDF and returns the updated contract with document_url set.
   * Requires VITE_API_URL.
   */
  async uploadDocument(id: string, file: File): Promise<BrokerageContract> {
    const formData = new FormData();
    formData.append("file", file);
    const res = await nestMultipartRequest<unknown>(API_ROUTES.brokerageContracts.document(id), formData);
    return brokerageContractSchema.parse(res);
  },

  /**
   * POST /brokerage-contracts/:id/links
   * (JSON { property_id, contract_type, unit_ids?, all_units? })
   *
   * AC2 — links a property to the contract via the junction table, scoped to
   * specific units (`unit_ids`) or the whole property (`all_units`/neither).
   * contract_type ∈ marketing | sales | management. The endpoint returns ONE
   * junction row per persisted link (an array); for backward tolerance a single
   * object is accepted and wrapped. Requires VITE_API_URL.
   */
  async linkProperty(
    id: string,
    link: LinkPropertyBrokerage,
  ): Promise<PropertyBrokerageLink[]> {
    const res = await nestRequest<unknown>(API_ROUTES.brokerageContracts.links(id), {
      method: "POST",
      body: JSON.stringify(link),
    });
    return z
      .union([
        z.array(propertyBrokerageLinkSchema),
        propertyBrokerageLinkSchema.transform((row) => [row]),
      ])
      .parse(res);
  },
};
